import { els } from './dom.js';
import { truncate } from './format.js';
import { getListingTitle } from './listing-data.js';
import { getImageLimit, normalizeVideoMode } from './rules.js';
import { createGenerationProgress } from '../../ui/generation-progress.js';

function currentVideoType() {
  return els.videoType()?.value || 'SEED_FAST';
}

function selectedMode(state) {
  return normalizeVideoMode(currentVideoType(), state?.mode || 'single');
}

function videoAssetUrl(item) {
  const id = item?.id || item?.assetId || item?.result?.assetId;
  return id ? `/api/video/assets/${encodeURIComponent(id)}/file` : '';
}

const VIDEO_PROGRESS_STEPS = [
  { key: 'queued', label: '排队' },
  { key: 'submit', label: '提交' },
  { key: 'poll', label: '生成' },
  { key: 'done', label: '保存' },
];

function normalizeTaskPhase(task = {}) {
  const status = String(task.status || '').toLowerCase();
  const phase = String(task.phase || '').toLowerCase();
  if (status === 'done' || status === 'completed') return 'done';
  if (status === 'failed') return 'error';
  if (phase === 'upload' || phase === 'submit' || phase === 'poll' || phase === 'queued') return phase;
  if (status === 'queued') return 'queued';
  if (status === 'running') return 'poll';
  return 'queued';
}

function elapsedMs(task = {}) {
  const start = task.createdAt || task.startedAt || task.updatedAt;
  const time = start ? new Date(start).getTime() : 0;
  if (!time || Number.isNaN(time)) return 0;
  return Math.max(0, Date.now() - time);
}

function videoProgressPercent(task = {}) {
  const phase = normalizeTaskPhase(task);
  if (phase === 'upload') return 12;
  if (phase === 'queued') return 18;
  if (phase === 'submit') return 34;
  if (phase === 'poll') {
    const slowAdvance = Math.min(40, Math.floor(elapsedMs(task) / 30000) * 5);
    return Math.min(88, 48 + slowAdvance);
  }
  if (phase === 'done') return 100;
  if (phase === 'error') return 100;
  return 8;
}

function videoProgressTitle(task = {}) {
  const phase = normalizeTaskPhase(task);
  if (phase === 'upload') return '正在上传参考图';
  if (phase === 'queued') return '视频任务已进入队列';
  if (phase === 'submit') return '正在提交 LinkFox';
  if (phase === 'poll') return 'LinkFox 正在生成视频';
  if (phase === 'done') return '视频生成完成';
  if (phase === 'error') return '视频生成失败';
  return '正在生成视频';
}

function videoProgressDetail(task = {}, detail = '') {
  const message = detail || task.progressMessage || task.message || task.error || '';
  if (message) return message;
  const phase = normalizeTaskPhase(task);
  if (phase === 'poll') return '视频通常需要等待远端生成与本地保存，完成后会自动切换为播放器。';
  return '请保持页面打开，系统会自动刷新任务状态。';
}

function parseMetadata(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

function compactTimestamp(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}

function videoDisplayName(item, meta = parseMetadata(item?.metadata)) {
  if (meta.displayName) return meta.displayName;
  if (meta.filename) return String(meta.filename).replace(/\.[^.]+$/, '');
  const inputMode = meta.inputMode || meta.input?.inputMode || item?.inputMode || '';
  const modeLabels = { single: '图生视频', firstLast: '首尾帧', multi: '多图参考' };
  const label = modeLabels[inputMode] || '图生视频';
  const stamp = compactTimestamp(item?.created_at || item?.createdAt || meta.generatedAt);
  return stamp ? `${label}_${stamp}` : label;
}

function createVideoElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function replaceVideoChildren(root, ...children) {
  if (!root) return;
  root.replaceChildren(...children.filter(Boolean));
}

function createVideoEmptyState(message) {
  return createVideoElement('div', 'video-empty', message);
}

export function renderBoundListing(listing) {
  const host = els.boundListing();
  if (!host) return;
  if (!listing) {
    replaceVideoChildren(host, createVideoElement('span', '', '未绑定 Listing。可直接上传图片生成；绑定后会归档到对应 Listing。'));
    return;
  }
  const title = getListingTitle(listing);
  const details = [
    createVideoElement('strong', '', listing.name || `Listing #${listing.id}`),
    title ? createVideoElement('span', '', truncate(title, 150)) : null,
    createVideoElement('span', '', `ID: ${listing.id ?? ''}${listing.marketplace ? ` · ${listing.marketplace}` : ''}`),
  ];
  replaceVideoChildren(host, ...details);
}

export function renderListingResults(listings, onSelect) {
  const host = els.listingResults();
  if (!host) return;
  if (!listings.length) {
    replaceVideoChildren(host, createVideoEmptyState('未找到可绑定的 Listing。'));
    return;
  }
  const cards = listings.map((listing) => {
    const title = getListingTitle(listing);
    const card = createVideoElement('button', 'video-listing-card');
    card.type = 'button';
    card.dataset.id = String(listing.id ?? '');
    card.append(createVideoElement('strong', '', listing.name || `Listing #${listing.id}`));
    if (title) card.append(createVideoElement('div', '', truncate(title, 90)));
    card.addEventListener('click', () => onSelect?.(card.dataset.id));
    return card;
  });
  replaceVideoChildren(host, ...cards);
}

export function renderReferenceImages(state) {
  const mode = selectedMode(state);
  const limit = getImageLimit(currentVideoType(), mode);
  if (state.referenceImages.length > limit) {
    state.referenceImages = state.referenceImages.slice(0, limit);
  }
  const label = els.referenceLabel();
  if (label) {
    const emptyText = mode === 'multi'
      ? `最多上传 ${limit} 张参考图，可少于上限`
      : (mode === 'firstLast' ? '上传首帧图' : '上传 1 张原图');
    label.textContent = state.referenceImages.length
      ? `已选择 ${state.referenceImages.length} 张，最多 ${limit} 张`
      : emptyText;
  }
  const host = els.referenceThumbs();
  if (!host) return;
  const thumbnails = state.referenceImages.map((item) => {
    const image = createVideoElement('img', 'video-thumb');
    image.src = item.dataUrl || '';
    image.alt = item.name || '参考图';
    return image;
  });
  replaceVideoChildren(host, ...thumbnails);
  renderReferenceSource(state);
}

export function renderReferenceSource(state) {
  const host = els.referenceSourceHint();
  if (!host) return;
  const count = state.referenceImages.length;
  if (!count) {
    host.textContent = state.listing
      ? '可上传本地图，也可从当前 Listing 已生成套图中选择。'
      : '可直接上传本地图生成视频；绑定 Listing 后可从套图中选择并归档结果。';
    return;
  }
  const source = state.referenceSource === 'listing_assets'
    ? '当前 Listing 套图'
    : '本地上传';
  host.textContent = `参考图来源：${source} · ${count} 张`;
}

export function renderPromptSource(state) {
  const host = els.promptSource();
  if (!host) return;
  const listingName = state.listing?.name || (state.listing?.id ? `Listing #${state.listing.id}` : '');
  const fromListing = state.promptSource === 'listing';
  const uploadWithListingPrompt = fromListing && state.referenceSource === 'upload' && state.referenceImages.length > 0;
  host.dataset.tone = uploadWithListingPrompt ? 'warn' : '';
  host.textContent = uploadWithListingPrompt
    ? `提示词仍来自绑定 Listing「${truncate(listingName, 50)}」。如果上传的本地图不是该商品，请先改写提示词或切换 Listing。`
    : (fromListing
        ? `提示词来源：绑定 Listing「${truncate(listingName, 60)}」`
        : '提示词来源：手动编辑');
}

export function renderPreview(task) {
  const host = els.preview();
  if (!host) return;
  host.removeAttribute('aria-busy');
  const videoUrl = videoAssetUrl(task) || task?.result?.videoUrl || task?.file_url || task?.fileUrl || '';
  if (!videoUrl) {
    replaceVideoChildren(host, createVideoEmptyState('视频生成后会在这里播放。'));
    return;
  }
  const video = document.createElement('video');
  video.src = videoUrl;
  video.controls = true;
  video.playsInline = true;
  video.preload = 'metadata';
  replaceVideoChildren(host, video);
}

export function renderPreviewNotice(message = '参数已变更，请重新生成视频。') {
  const host = els.preview();
  if (!host) return;
  host.removeAttribute('aria-busy');
  replaceVideoChildren(host, createVideoEmptyState(message));
}

export function renderGenerationProgress(task = {}, { detail = '', tone = '' } = {}) {
  const host = els.preview();
  if (!host) return;
  const phase = normalizeTaskPhase(task);
  const resolvedTone = tone || (phase === 'error' ? 'error' : (phase === 'done' ? 'success' : 'running'));
  const percent = videoProgressPercent(task);
  host.setAttribute('aria-busy', resolvedTone === 'running' ? 'true' : 'false');
  replaceVideoChildren(host, createGenerationProgress({
    title: videoProgressTitle(task),
    detail: videoProgressDetail(task, detail),
    footer: resolvedTone === 'running'
      ? '页面会自动轮询任务状态，生成完成后自动加载视频；此过程可能持续数分钟。'
      : videoProgressDetail(task, detail),
    tone: resolvedTone,
    phase,
    percent,
    steps: VIDEO_PROGRESS_STEPS,
  }));
}

export function renderHistory(videos = []) {
  const host = els.history();
  if (!host) return;
  if (!videos.length) {
    replaceVideoChildren(host, createVideoEmptyState('这个 Listing 还没有生成过视频。'));
    return;
  }
  const cards = videos.map((item) => {
    const metadata = parseMetadata(item.metadata);
    const url = videoAssetUrl(item) || item.file_url || item.fileUrl || '';
    const status = item.status || '';
    const credits = Number(item.cost_tokens || item.costTokens || 0) || 0;
    const meta = [item.model, item.duration ? `${item.duration}s` : '', item.resolution || status, credits ? `${credits}算力` : ''].filter(Boolean).join(' · ');
    const title = videoDisplayName(item, metadata);
    const card = createVideoElement('article', 'video-card');
    if (url) {
      const video = createVideoElement('video', 'video-lazy');
      video.dataset.src = url;
      video.controls = true;
      video.playsInline = true;
      video.preload = 'none';
      card.append(video);
    } else {
      card.append(createVideoEmptyState('处理中'));
    }
    card.append(
      createVideoElement('div', 'video-card-meta', title),
      createVideoElement('div', 'video-card-meta', meta || '视频任务'),
    );
    if (item.error_message) card.append(createVideoElement('div', 'video-card-meta', item.error_message));
    return card;
  });
  replaceVideoChildren(host, ...cards);
  // 历史视频懒加载：进入视口才设置 src 真正解码（9 代 i3 核显优化）
  host.querySelectorAll('video.video-lazy').forEach((v) => {
    if (!('IntersectionObserver' in window)) {
      v.src = v.dataset.src;
      v.removeAttribute('data-src');
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          el.src = el.dataset.src;
          el.removeAttribute('data-src');
          el.classList.remove('video-lazy');
          io.unobserve(el);
        }
      });
    }, { rootMargin: '200px' });
    io.observe(v);
  });
}
